import { useTranslations } from 'next-intl';
import { type FC, useCallback, useMemo } from 'react';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';

import { useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';

import { z } from 'zod';

import { StepLayout } from '@/app/[locale]/(not-authorized)/account/login/components/Login/components/TwoFaSetup/components/StepLayout';
import { Flex } from '@/components/common/Flex';
import { FormTextInput } from '@/components/common/form/FormTextInput';
import { useCommonApolloOptions } from '@/hooks/use-common-apollo-options';
import type { StepParams } from '@/hooks/use-stepper';

import { gql } from '../../../../../../../../../../../../generated';

type CodeStepProps = {
  stepParams: StepParams;
  goToStep0: () => unknown;
  onSubmit: () => unknown;
};

const useFormSchema = () => {
  const tErrors = useTranslations('errors');

  return useMemo(
    () =>
      z.object({
        code: z.string().min(1, tErrors('field is required')),
      }),
    [tErrors]
  );
};

export type CodeFormData = z.infer<ReturnType<typeof useFormSchema>>;

const Activate2Fa = gql(/* GraphQL */ `
  mutation Activate2Fa($token: String!) {
    otpActivate(token: $token)
  }
`);

export const CodeStep: FC<CodeStepProps> = (props) => {
  const t = useTranslations('pages.account.login.form.setup2FaBlock.steps.setup.steps.code');

  const { stepParams, goToStep0, onSubmit } = props;

  const form = useForm<CodeFormData>({
    resolver: zodResolver(useFormSchema()),
    mode: 'onBlur',
  });

  const { register, handleSubmit } = form;

  const apolloOptions = useCommonApolloOptions();

  const [activate] = useMutation(Activate2Fa, apolloOptions);

  const activate2Fa = useCallback<SubmitHandler<CodeFormData>>(
    async (data) => {
      const { data: result } = await activate({
        variables: {
          token: data.code,
        },
      });

      if (!result) {
        return;
      }

      onSubmit();
    },
    [activate, onSubmit]
  );

  return (
    <StepLayout
      stepParams={stepParams}
      goToStep0={goToStep0}
      renderForm={({ buttons }) => (
        <FormProvider {...form}>
          <form onSubmit={handleSubmit(activate2Fa)}>
            <Flex direction="column" gap={4}>
              <FormTextInput {...register('code')} required placeholder={t('fields.code.placeholder')} />

              {buttons}
            </Flex>
          </form>
        </FormProvider>
      )}
    />
  );
};
